import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import type { Schema } from '../../amplify/data/resource';
import outputs from '../../amplify_outputs.json';

/**
 * Amplify client bootstrapping.
 *
 * `amplify_outputs.json` is generated by `ampx pipeline-deploy` or
 * `ampx generate outputs --branch <branch> --app-id <appId>` and is
 * gitignored (it contains per-environment Cognito/AppSync identifiers).
 *
 * The file is imported statically so Vite inlines its contents at build
 * time — the production bundle never issues a runtime fetch for it. A
 * TypeScript module declaration in `src/types/amplify-outputs.d.ts`
 * keeps `tsc --noEmit` happy when the file is briefly missing on disk.
 *
 * CI generates the file before `pnpm type-check` and `vite build`; see
 * `.github/workflows/app-ci.yml` and `app/amplify.yml` for the exact
 * step ordering.
 */

let configured = false;

/**
 * Synchronously configure Amplify. Safe to call multiple times; only the
 * first call has any effect. Called at module-top-level in `main.tsx`
 * before the first React render, guaranteeing the GraphQL client is
 * configured before any hook fires a query.
 */
export function configureAmplify(): void {
  if (configured) return;

  // When `ampx generate outputs` has never been run locally, the file
  // may be an empty `{}` placeholder (see the CI workflow). Detect that
  // and surface a loud warning instead of a cryptic runtime failure.
  if (!outputs || Object.keys(outputs).length === 0) {
    // eslint-disable-next-line no-console
    console.warn(
      '[amplify] amplify_outputs.json is empty; run `pnpm exec ampx generate outputs --branch staging --app-id <appId>` to populate it. GraphQL calls will fail until then.',
    );
    configured = true;
    return;
  }

  // Amplify Gen 2's generated outputs reference the Cognito prefix domain,
  // but production uses a custom domain (auth.pullitpacks.com). Override
  // the OAuth domain at configure time so Google SSO redirects correctly.
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'pullitpacks.com' || window.location.hostname === 'www.pullitpacks.com')
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
    if ((outputs as any).auth?.oauth) (outputs as any).auth.oauth.domain = 'auth.pullitpacks.com';
  }

  Amplify.configure(outputs as Parameters<typeof Amplify.configure>[0]);
  configured = true;
}

/**
 * AppSync GraphQL client, typed from the backend `Schema`.
 *
 * Usage:
 *   const { data, errors } = await client.queries.listActivePacks();
 */
export const client = generateClient<Schema>();
